import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import { availabilitySlots, parseAvailabiliy } from '@/constants/availability'
import type { DeepCamelCase } from '@/types/utils'
import { idAndNameSchema, requiredString, roomType } from '@/utils/zod'

const availabilitySchema = z.object({
  id: requiredString(),
  monday: availabilitySlots,
  tuesday: availabilitySlots,
  wednesday: availabilitySlots,
  thursday: availabilitySlots,
  friday: availabilitySlots,
  saturday: availabilitySlots,
  sunday: availabilitySlots
})

const responseSchema = z.object({
  id: requiredString(),
  name: requiredString(),
  capacity: z.number().nullable(),
  room_type: roomType,
  special_equipment: z.string().nullable(),
  preferred_classes: z.array(idAndNameSchema),
  preferred_courses: z.array(
    z.object({
      id: requiredString(),
      display_name: requiredString()
    })
  ),
  preferred_users: z.array(idAndNameSchema),
  availability: availabilitySchema,
  can_be_deleted: z.boolean()
})

type APIResponse = z.infer<typeof responseSchema>
type APIResponseCamelCase = DeepCamelCase<APIResponse>

export type RoomDetails = Omit<APIResponseCamelCase, 'preferredCourses'> & {
  preferredCourses: {
    id: string
    name: string
  }[]
}

const parseResponse = (response: APIResponse) => ({
  id: response.id,
  name: response.name,
  capacity: response.capacity,
  roomType: response.room_type,
  specialEquipment: response.special_equipment,
  preferredClasses: response.preferred_classes,
  preferredCourses: response.preferred_courses.map(course => ({
    id: course.id,
    name: course.display_name
  })),
  preferredUsers: response.preferred_users,
  availability: {
    id: response.availability.id,
    monday: parseAvailabiliy(response.availability.monday),
    tuesday: parseAvailabiliy(response.availability.tuesday),
    wednesday: parseAvailabiliy(response.availability.wednesday),
    thursday: parseAvailabiliy(response.availability.thursday),
    friday: parseAvailabiliy(response.availability.friday),
    saturday: parseAvailabiliy(response.availability.saturday),
    sunday: parseAvailabiliy(response.availability.sunday)
  },
  canBeDeleted: response.can_be_deleted
})

export const roomQueryOptions = (roomId: string) => ({
  queryKey: ['panelRoomsRetrieve', roomId],
  staleTime: 60 * 1000,
  queryFn: async () => {
    const response = await httpService.panel.panelRoomsRetrieve(roomId, {
      fetchKeys: {
        id: true,
        name: true,
        availability: true,
        capacity: true,
        room_type: true,
        special_equipment: true,
        preferred_classes: true,
        preferred_courses: true,
        preferred_users: true,
        can_be_deleted: true
      }
    })
    return parseResponse(responseSchema.parse(response))
  }
})

export const useRoom = (roomId: string) => useQuery(roomQueryOptions(roomId))
